import { registerSafeSpeaker } from '../../app/SafeModeBoundary';
import { useUiStore } from '../../app/uiStore';
import { getSettings } from '../profile/ProfileStore';
import { createDirectTouchAdapter } from '../input/adapters/DirectTouch';
import { createDwellAdapter } from '../input/adapters/Dwell';
import { createScanningAdapter } from '../input/adapters/Scanning';
import type { InputAdapter, InputModeId } from '../input/InputAdapter';
import { useAudioStatus } from './audioStatusStore';
import { createAzureTtsProvider } from './providers/AzureTts';
import { createCachedAudioProvider } from './providers/CachedAudio';
import { createPiperLocalProvider } from './providers/PiperLocal';
import { createVisualOnlyProvider } from './providers/VisualOnly';
import { createWebSpeechProvider } from './providers/WebSpeech';
import { usePiper } from './piper/usePiper';
import { speechService } from './SpeechService';
import { warmUpTts } from './warmup';

/**
 * Assemble the speech stack. One place where concrete providers are named — everything above this
 * sees only `SpeechService` (the layering rule, CON-10: if a component knows which TTS engine is active,
 * that is a bug).
 *
 * Registration order does not matter; `SpeechService` imposes the chain order itself. `piperLocal` was simply
 * absent in Phase 1 and arrives in Phase 2 stage 4 with **no edit to `SpeechService`** — which is the whole point
 * of resolving from registered providers rather than a fixed list (C9).
 */

export const directTouchAdapter = createDirectTouchAdapter(() => {
  const settings = getSettings();
  return {
    slopPx: settings.slopPx,
    repeatSuppressMs: settings.repeatSuppressMs,
    keyRepeatSuppressMs: settings.keyRepeatSuppressMs,
    touchOffsetCorrection: settings.touchOffsetCorrection,
  };
});

/**
 * Dwell (3.1). Reads its settings live on every event rather than closing over them, which is what makes
 * the phase gotcha satisfiable: "every timing value must be adjustable **while the person is using it.**"
 * A caregiver moving the `dwellMs` slider sees the next press respond, with no reload and no remount.
 */
export const dwellAdapter = createDwellAdapter(() => {
  const settings = getSettings();
  return {
    dwellMs: settings.dwellMs,
    slopPx: settings.slopPx,
    repeatSuppressMs: settings.repeatSuppressMs,
    keyRepeatSuppressMs: settings.keyRepeatSuppressMs,
    touchOffsetCorrection: settings.touchOffsetCorrection,
  };
});

/**
 * Scanning (3.3). Reads `scanIntervalMs` and `scanLoops` live on every tick, so a caregiver tuning either sees
 * the effect on the next step — the phase gotcha requires exactly that.
 */
export const scanningAdapter = createScanningAdapter(() => {
  const settings = getSettings();
  return { scanIntervalMs: settings.scanIntervalMs, scanLoops: settings.scanLoops };
});

/**
 * The adapter for a mode, and the one place that mapping exists.
 *
 * Two ids land on direct touch and they are not the same kind of thing (`INP-13`).
 *
 * `partnerAssisted` falls through **by design**, on every activation rather than never: its surface narrows
 * the screen to two half-screen targets, and direct touch is the right way to press those. The picker offers
 * it (`CFG-03`).
 *
 * `hardwareKeyboard` is the one the picker disables, so reaching *this* case for it is a bug — and a bug that
 * made the screen inert would be much worse than one that made it behave like direct touch. Note what is
 * missing there is a *mode*, not the switch: a Bluetooth switch presents as a keyboard, and the scanning
 * adapter already listens for Space and Enter.
 */
export function adapterFor(mode: InputModeId): InputAdapter {
  switch (mode) {
    case 'dwell':
      return dwellAdapter;
    case 'scanning':
      return scanningAdapter;
    case 'directTouch':
    case 'partnerAssisted':
    case 'hardwareKeyboard':
      return directTouchAdapter;
  }
}

export function setupSpeech(): void {
  useAudioStatus.getState().init();

  const getVoiceId = (): string => getSettings().voiceId;

  speechService.register(createCachedAudioProvider(getVoiceId));
  speechService.register(
    createAzureTtsProvider({
      getVoiceId,
      isCloudEnabled: () => getSettings().cloudTtsEnabled,
    }),
  );
  // Below azureTts and above webSpeech, which is what makes the offline voice a real voice rather than the
  // platform's. It reports itself unavailable until the model is loaded, so registering it unconditionally is
  // safe — and `usePiper.start()` does nothing at all while `piperEnabled` is false.
  speechService.register(createPiperLocalProvider());
  void usePiper.getState().start();

  speechService.register(createWebSpeechProvider());
  speechService.register(
    createVisualOnlyProvider(() => {
      // Total speech failure pushes the partner view up so the sentence can at least be read.
      useUiStore.getState().forceTo('partner', 'speechFailure');
    }),
  );

  // Safe mode speaks through the real chain when the chain is healthy, and falls back to
  // `speechSynthesis` on its own if this registration is what broke.
  registerSafeSpeaker((text) => {
    void speechService.speak({ text, source: 'keyboard' });
  });

  void warmUpTts();
}
